import { useState } from 'react'
// Kebab uses lucide's horizontal ellipsis by design — the Phosphor kebab is too
// thin; this is the documented exception to the Phosphor-icon rule. Matches the
// homepage pin menus. Submenu carets stay Phosphor.
import { Ellipsis } from 'lucide-react'
import { PiCaretLeft, PiCaretRight } from 'react-icons/pi'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'

export type WidgetMenuItem = {
  label: string
  onSelect?: () => void
  /** When set, selecting this item drills into a secondary list of items. */
  submenu?: WidgetMenuItem[]
}

const DEFAULT_ITEMS: WidgetMenuItem[] = [
  { label: 'View More' },
  { label: 'Delete' },
]

/**
 * The kebab "more options" affordance shared by the dashboard widgets. Built on
 * our Popover + lucide icon + design tokens. Items default to View More /
 * Delete; pass `items` to customize. An item with a `submenu` drills into a
 * secondary list (e.g. "Add to report" → your saved reports) with a back arrow.
 *
 * All clicks stop propagation so the menu works inside a clickable card (a tile
 * that links to its report) without triggering navigation.
 */
export function WidgetMenu({
  items = DEFAULT_ITEMS,
  className,
}: {
  items?: WidgetMenuItem[]
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [submenu, setSubmenu] = useState<WidgetMenuItem | null>(null)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSubmenu(null) // reset drill-in when the menu closes
  }

  const renderItem = (item: WidgetMenuItem) => (
    <button
      key={item.label}
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        if (item.submenu) {
          setSubmenu(item)
        } else {
          item.onSelect?.()
          setOpen(false)
          setSubmenu(null)
        }
      }}
      className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg"
    >
      <span className="truncate">{item.label}</span>
      {item.submenu && <PiCaretRight size={14} className="shrink-0" />}
    </button>
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Widget options"
          // Stop the click reaching an enclosing clickable card (e.g. a tile
          // that links to its report) so the menu opens without navigating.
          // preventDefault also suppresses Radix's own toggle (it skips on a
          // default-prevented event), so we open the popover manually here.
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setOpen(true)
          }}
          // Keep press-and-hold on the kebab from starting a tile drag.
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            'flex size-7 items-center justify-center rounded-md text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg',
            className
          )}
        >
          <Ellipsis size={18} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1">
        {submenu ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setSubmenu(null)
              }}
              className="mb-1 flex w-full items-center gap-1 rounded-lg px-2 py-1.5 text-left text-xs font-medium text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg"
            >
              <PiCaretLeft size={14} className="shrink-0" />
              {submenu.label}
            </button>
            <div className="flex flex-col">
              {(submenu.submenu ?? []).map(renderItem)}
            </div>
          </>
        ) : (
          <div className="flex flex-col">{items.map(renderItem)}</div>
        )}
      </PopoverContent>
    </Popover>
  )
}
